import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Play, Pause, SkipForward, SkipBack, X, Volume2, VolumeX, RefreshCw, Sparkles, Loader2,
} from "lucide-react";
import { api } from "../lib/api";

/**
 * DemoTourOverlay — self-running product tour that drives the LIVE app.
 *
 * Audio pipeline (2026-02-14 revamp):
 *   • Each scene's caption is synthesized by `POST /api/demo/tts` which
 *     calls OpenAI's TTS (nova, female / energetic) via the Emergent
 *     Universal LLM key. Server caches by (text, voice, speed) so
 *     restarts / replays are instant.
 *   • The overlay preloads the NEXT scene's audio while the CURRENT
 *     scene's audio is playing — this eliminates the audible "gap"
 *     between scenes.
 *   • If the backend TTS fails (network / quota), we fall back to the
 *     browser's built-in `speechSynthesis` with a female English voice
 *     when the platform ships one. Muting stops the audio but keeps
 *     scenes advancing on a fixed-duration timer.
 *
 * Navigation pipeline:
 *   • Each scene dispatches a `demo:goto` `CustomEvent` handled by
 *     `App.js` (`{tab, pickAthlete}`) — no prop-drilling of setTab.
 *   • Optional `click_testid` clicks a `data-testid` element to open
 *     the target modal.
 *   • On scene exit, `close_testid` closes the modal opened during
 *     entry so we don't stack modals across scenes.
 */

// Scene script — kept intentionally rich so nutritionists and coaches
// get a proper capability walk-through, not just a screensaver.
// Duration is a floor; audio `onended` advances earlier when narration
// naturally finishes (feels much tighter than a fixed dwell).
const SCENES = [
  {
    key: "intro",
    tab: null,
    title: "Welcome",
    caption:
      "Welcome to the Yacht Club of Hyderabad athlete health dashboard. This is an integrated medical, biometric and nutrition operations platform for competitive sailing squads, layered with AI-driven decision support at every step. In the next few minutes you will see how coaches, medical officers and nutritionists collaborate around a single source of truth for every young athlete.",
  },
  {
    key: "overview",
    tab: "overview",
    title: "Insights · Overview",
    caption:
      "The insights overview is the coach's morning check. It shows total athletes tracked, latest lab and biometric coverage, the top current deficiency across the squad, and how many athletes are on medical rest today. Everything is a click away.",
  },
  {
    key: "urgent",
    tab: "overview",
    click_testid: "open-urgent-btn",
    close_testid: "urgent-close-btn",
    title: "Urgent Issues Checklist",
    caption:
      "The urgent checklist ranks the ten most alarming persistent issues across the squad, weighted by clinical importance and how long each issue has been outstanding. One click prints it and hands it to the medical officer.",
  },
  {
    key: "briefing",
    tab: "overview",
    click_testid: "open-briefing-btn",
    close_testid: "briefing-close",
    title: "Daily AI Briefing",
    caption:
      "Every morning, an AI briefing — powered by Claude — reads the whole squad's latest labs, injuries, missed appointments and upcoming schedules and summarises the top three athletes to check today. It prioritises the coach's time so the highest-risk sailor is never buried under paperwork.",
  },
  {
    key: "weather",
    tab: "overview",
    title: "Squad Weather Map",
    caption:
      "Scrolling down, the squad weather map pivots up to seventy-five blood markers across every athlete. Green means in range, amber and red flag values that need review. Coaches immediately see squad-wide patterns like iron deficiency or vitamin D shortfall.",
  },
  {
    key: "editor",
    tab: "editor",
    pick_athlete: true,
    title: "Data Editor · Timeline",
    caption:
      "The data editor is the athlete's chronological timeline. Lab tests, consultations, supplements, deworming doses and injuries all live in one autosaving spreadsheet, with inline edits and a full audit trail of who changed what.",
  },
  {
    key: "trend",
    tab: "sailor",
    pick_athlete: true,
    title: "Athlete Trend Snapshot",
    caption:
      "The trend view is a per-athlete health snapshot. Latest values sit next to squad percentiles so a coach knows immediately whether this sailor is above or below the norm for their age and squad.",
  },
  {
    key: "marker-trends",
    tab: "marker_trends",
    pick_athlete: true,
    title: "Marker Trends · Multi-Blood-Marker Chart",
    caption:
      "Marker Trends is one of the most powerful views in the app. It plots any combination of blood markers on a single line chart over time — ferritin, vitamin D, haemoglobin, B12, thyroid, lipid panel, kidney and liver markers — up to seventy-five in all. The violet dots along the bottom axis mark exactly when supplements were administered. This lets the coach visually verify whether ferritin actually responded to the iron dose, whether vitamin D climbed after a supplement course, or whether markers rebounded after a stoppage.",
  },
  {
    key: "biometrics",
    tab: "biometrics",
    title: "Biometrics Matrix",
    caption:
      "The biometrics matrix is a spreadsheet of every weigh-in. Green cells mark weight gains, red cells mark weight loss or unexpected height drops. A single glance reveals which athletes are trending in the wrong direction — the coach doesn't have to open each profile individually.",
  },
  {
    key: "biometrics-trend",
    tab: "biometrics",
    dblclick_name_contains: "Badrinath",
    close_testid: "bio-trend-close",
    title: "Athlete Biometric Trend · Height + Weight + BMI",
    caption:
      "Double-clicking any athlete's name in the biometrics matrix opens their personal growth chart. Here you're looking at Badrinath's timeline — height climbs as cyan bars, weight rides as a white line on top, and BMI runs as a thin secondary line, all on the same axes. A coach can see at a glance whether a sailor is growing taller without gaining weight, gaining weight without growing, or tracking in a healthy balanced ratio. A translucent aspiration cap on the last height bar shows how much further Badrinath is projected to grow before skeletal maturity. This single graph replaces flipping between spreadsheets and gives the coach the whole biometric story of one athlete in one view.",
  },
  {
    key: "biometrics-graph",
    tab: "biometrics",
    click_testid: "growth-chart-btn",
    close_testid: "growth-chart-close",
    title: "Squad Growth Chart · vs IAP 2015 Norms",
    caption:
      "Zooming out from the individual athlete, the squad growth chart plots every sailor's height and weight together against the Indian Academy of Pediatrics 2015 reference curves. The shaded bands mark the third, tenth, fiftieth, ninetieth and ninety-seventh percentiles for Indian children — so a coach can immediately see which sailors are tracking above the norm, at the norm, or falling behind their peer group. Height and weight are overlaid on the same axes to reveal squad-wide patterns — for example, a cohort that is tall but under-weight signals a nutrition gap the coach and nutritionist need to close together.",
  },
  {
    key: "height-projection",
    tab: "biometrics",
    click_testid: "height-projection-btn",
    close_testid: "height-projection-close",
    title: "Predictive Heights · AI-Assisted Growth Forecast",
    caption:
      "Predictive heights is a forward-looking projection powered by longitudinal growth modelling. For every athlete, the system takes their measured growth velocity, their current age, their gender, and their placement on the Indian Academy of Pediatrics reference curve, then projects where their height is likely to plateau at skeletal maturity. This gives the coach an early view of which sailors will grow into a taller class — critical for dinghy assignment years in advance. The projection is refined every time a new measurement is entered, so the estimate sharpens with each session.",
  },
  {
    key: "weight-highlights",
    tab: "biometrics",
    click_testid: "weight-highlights-btn",
    close_testid: "wh-close",
    title: "Weight Highlights",
    caption:
      "After every weigh-in, the weight highlights report ranks the top ten gainers and top ten losers since each athlete's previous reading. It's the fastest way to shortlist athletes for the nutritionist and the medical officer.",
  },
  {
    key: "growth-advisory",
    tab: "biometrics",
    click_testid: "growth-advisory-btn",
    close_testid: "ga-close",
    title: "Growth Advisory · AI Nutrition Verdicts",
    caption:
      "The growth advisory answers three questions at once — using AI-driven analysis on top of every biometric and lab reading. When do our sailors typically grow, who is going through a spurt right now, and are we supplementing them adequately for it. Clicking any athlete expands an AI-generated nutrition verdict cross-referencing their active supplements against their latest ferritin, vitamin D, haemoglobin and B12 values.",
  },
  {
    key: "diet",
    tab: "diet",
    title: "Squad Diet · AI-Powered Menu",
    caption:
      "The squad diet tab holds an editable weekly menu with computed macronutrients and micronutrients per dish. AI-powered menu-marker gap analysis, powered by Claude, links each low blood marker across the squad to specific dishes that fill the nutritional shortfall — so the kitchen knows exactly what to cook this week to lift the whole squad's iron, vitamin D and protein intake.",
  },
  {
    key: "reports",
    tab: "reports",
    title: "Ops · Reports",
    caption:
      "The ops reports section holds coach-friendly printable reports. Baseline coverage shows which core fields are missing per athlete so the coach can chase them down without scrolling through every profile.",
  },
  {
    key: "schedule",
    tab: "schedule",
    title: "Appointments & Reminders",
    caption:
      "The schedule tab drives blood-test cadence, deworming intervals and consultation follow-ups. Every athlete has a schedule cluster derived from their training level, and overdue items surface as automatic reminders.",
  },
  {
    key: "bulk",
    tab: "bulk",
    title: "Bulk Lab Import · Deterministic + AI Vision Parser",
    caption:
      "Bulk lab import accepts an entire folder of PDF lab reports at once. A deterministic parser handles the common Indian lab formats, and any unusual layout falls back to Claude's vision model for AI-driven extraction. The system auto-matches each PDF to the right athlete by name, deduplicates repeat rows, and archives the original PDF in the database — a full pipeline from paper to structured data in seconds.",
  },
  {
    key: "masters",
    tab: "overview",
    open_route: "menu:schools",
    close_testid: "schools-close",
    title: "Masters Setup · Schools, Squads, Classes, Levels",
    caption:
      "The masters section is where admins configure every reference table the app depends on — schools the athletes come from, squads, sailing classes, training levels, panel doctors, and per-panel reference ranges for blood markers. Here you're looking at the schools master; sibling modals cover squads, classes, levels, doctors and reference ranges. Everything downstream in the app — from the squad weather map to the athlete profile pill colours — respects these definitions, so admins only maintain the source of truth in one place.",
  },
  {
    key: "wearables",
    tab: "wearables",
    title: "Wearables Preview",
    caption:
      "The wearables tab previews an integration with WHOOP for recovery, strain and sleep telemetry, so coaches can correlate readiness against training load and blood work in a single view.",
  },
  {
    key: "outro",
    tab: "overview",
    title: "Purpose-built for competitive sailing",
    caption:
      "That is the tour. Real-time medical, biometric and nutrition data, layered with AI where AI adds value — audited, actionable, and always with the athlete's context one click away. Thank you for watching. To learn more, get in touch with the Yacht Club of Hyderabad coaching team.",
  },
];

// Small helper — POST to `/api/demo/tts` and return an object URL for
// an `<audio>` src. Falls back to `null` when the backend refuses
// (auth expired, quota, disabled), letting the caller drop to browser
// `speechSynthesis`.
async function fetchSceneAudio(text) {
  try {
    const r = await api.post(
      "/demo/tts",
      // Slower speed (0.95 vs default 1.0) — coaches asked for a
      // less-hurried cadence so the narration lines up with them
      // actually looking at the feature being described.
      { text, voice: "nova", speed: 0.95 },
      { responseType: "arraybuffer" }
    );
    const blob = new Blob([r.data], { type: "audio/mpeg" });
    return URL.createObjectURL(blob);
  } catch (e) {
    console.warn("[demo] TTS unavailable, falling back to browser synth", e);
    return null;
  }
}

export default function DemoTourOverlay({ open, onClose }) {
  const [idx, setIdx]         = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted]     = useState(false);
  const [preloading, setPreloading] = useState(false);
  // Set to true after a scene's narration finishes — the overlay then
  // shows a "Press SPACE (or ▶) to continue" prompt and holds until the
  // user acknowledges. Coaches asked for this so they can look at each
  // feature at their own pace instead of the tour racing ahead. Reset
  // to false whenever we actually advance to a new scene.
  const [awaitingAdvance, setAwaitingAdvance] = useState(false);

  // Cache of scene index → object URL for the pre-generated MP3, so
  // switching scenes is instant. Populated by an async preloader that
  // runs one step ahead of the currently-playing scene.
  const audioUrlsRef = useRef({});
  // Live <audio> element used for the currently-playing scene. Kept
  // in a ref so React re-renders don't reset playback.
  const audioRef     = useRef(null);
  const timerRef     = useRef(null);
  // Generation counter — bumped whenever we begin cleanup or start a
  // new scene, so any async work started under an OLDER generation
  // (e.g. a preload that resolves after the user hit "next") knows to
  // bail out instead of spawning a second concurrent audio element.
  // This is the fix for the "two voices playing out of phase" bug the
  // coach flagged: the useEffect re-ran on `playing`/`muted` toggles,
  // triggering enterScene twice; both invocations reached `new Audio()`
  // but only the last was tracked in `audioRef.current`, so cleanup
  // paused the newer one and the older kept talking.
  const genRef       = useRef(0);
  const scene = SCENES[idx];
  const total = SCENES.length;

  const clearAll = useCallback(() => {
    // Invalidate any in-flight enterScene call so its post-await work
    // is discarded instead of launching a stale <audio>.
    genRef.current += 1;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.src = ""; } catch {/* noop */}
      audioRef.current = null;
    }
    try { window.speechSynthesis?.cancel(); } catch {/* noop */}
  }, []);

  // Preload the next scene's audio while the current scene is
  // playing — this is the fix for the audible "pause between scenes"
  // the coach flagged. Cheap: server caches by text, so repeats are
  // instant. Concurrent to whatever's on screen.
  const preloadAudio = useCallback(async (targetIdx) => {
    if (targetIdx < 0 || targetIdx >= SCENES.length) return null;
    if (audioUrlsRef.current[targetIdx]) return audioUrlsRef.current[targetIdx];
    setPreloading(true);
    const url = await fetchSceneAudio(SCENES[targetIdx].caption);
    setPreloading(false);
    if (url) audioUrlsRef.current[targetIdx] = url;
    return url;
  }, []);

  const advance = useCallback((step) => {
    // Advancing always dismisses the "press space" prompt.
    setAwaitingAdvance(false);
    setIdx((cur) => {
      const next = cur + step;
      if (next < 0) return 0;
      if (next >= SCENES.length) {
        // End of tour — hard-stop every audio source RIGHT NOW so the
        // outro voice doesn't linger, and close on the next tick. Prior
        // to this we relied on the parent's `open=false` cleanup, but a
        // couple of coaches reported the narration looped back to
        // scene 1 audio while the visuals stayed on the outro screen —
        // that's the play/pause / speech-synthesis effects firing on
        // the same-idx re-render before the parent had a chance to
        // unmount us. Explicit teardown here fixes it.
        clearAll();
        setTimeout(() => onClose?.(), 200);
        return cur;
      }
      return next;
    });
  }, [onClose, clearAll]);

  const enterScene = useCallback(async (s, sceneIdx) => {
    clearAll();
    setAwaitingAdvance(false);
    const gen = genRef.current;

    // 1) Switch tab if needed.
    if (s.tab) {
      window.dispatchEvent(new CustomEvent("demo:goto", {
        detail: { tab: s.tab, pickAthlete: !!s.pick_athlete },
      }));
    }
    // 2) Close any modal the previous scene may have left open (but
    //    NOT this scene's own close button — we want that modal to
    //    stay visible while we narrate it).
    ["ms-close", "wh-close", "ga-close", "urgent-close-btn",
     "briefing-close", "growth-chart-close", "height-projection-close",
     "bio-trend-close", "schools-close"].forEach((tid) => {
      if (tid === s.close_testid) return;
      const el = document.querySelector(`[data-testid="${tid}"]`);
      if (el) { try { el.click(); } catch {/* noop */} }
    });
    // 3) Click the scene's entry-point button (opens the modal, etc.)
    //    with a small beat so the tab render lands first.
    if (s.click_testid) {
      setTimeout(() => {
        if (gen !== genRef.current) return;
        document.querySelector(`[data-testid="${s.click_testid}"]`)?.click();
      }, 400);
    }
    // 3b) Double-click a selector (used for entries that only open via
    //     dblclick, e.g. an athlete row in the biometrics matrix). A
    //     synthetic MouseEvent works with React's onDoubleClick handler
    //     as long as we bubble and pass a matching `detail` count.
    if (s.dblclick_selector) {
      setTimeout(() => {
        if (gen !== genRef.current) return;
        const el = document.querySelector(s.dblclick_selector);
        if (!el) return;
        try {
          el.dispatchEvent(new MouseEvent("dblclick", { bubbles: true, cancelable: true, view: window, detail: 2 }));
        } catch {/* noop */}
      }, 500);
    }
    // 3b-ii) Double-click a biometrics-matrix athlete row by name —
    //        used to pick a specific sailor (e.g. Badrinath) for the
    //        biometric trend scene. Scans every `bio-name-*` cell and
    //        picks the first whose visible text contains the target
    //        (case-insensitive). Falls back to the first cell if no
    //        match — better to show *some* athlete than nothing.
    if (s.dblclick_name_contains) {
      const target = s.dblclick_name_contains.toLowerCase();
      const tryDblclick = (attempt = 0) => {
        if (gen !== genRef.current) return;
        const cells = document.querySelectorAll('[data-testid^="bio-name-"]');
        let picked = null;
        for (const c of cells) {
          if ((c.textContent || "").toLowerCase().includes(target)) { picked = c; break; }
        }
        if (!picked && cells.length > 0) picked = cells[0];
        if (!picked) {
          // Biometrics grid still loading — retry a few times.
          if (attempt < 6) setTimeout(() => tryDblclick(attempt + 1), 400);
          return;
        }
        try {
          picked.dispatchEvent(new MouseEvent("dblclick", { bubbles: true, cancelable: true, view: window, detail: 2 }));
        } catch {/* noop */}
      };
      setTimeout(() => tryDblclick(), 500);
    }
    // 3c) Dispatch a `ych:open` route event — used for masters &
    //     bulk-action modals that live in the UserMenu event bus
    //     rather than a top-level tab.
    if (s.open_route) {
      setTimeout(() => {
        if (gen !== genRef.current) return;
        window.dispatchEvent(new CustomEvent("ych:open", { detail: { route: s.open_route } }));
      }, 400);
    }

    // 4) Preload the next scene's MP3 in parallel — instant advance.
    preloadAudio(sceneIdx + 1);

    // 5) Wait for the visual to actually LAND before starting the
    //    voice. Coaches reported the audio was racing ahead of the
    //    screen — this ~1.5 s beat gives the tab / modal time to
    //    paint so the narration lines up with what they're seeing.
    //    Modal scenes need a hair longer since the click has to fire
    //    and the modal has to fetch its data.
    const preAudioDelay = (s.click_testid || s.dblclick_selector || s.dblclick_name_contains || s.open_route) ? 1600 : 1200;
    await new Promise((res) => setTimeout(res, preAudioDelay));
    if (gen !== genRef.current) return;

    // 6) Play the pre-generated audio if muted is off. Fall back to
    //    browser TTS when the backend didn't have anything for us.
    if (muted) {
      // Silent mode: no auto-advance either — coach still uses SPACE
      // or the ▶ Next button. Show the "press space" prompt right
      // away so the intent is clear.
      setAwaitingAdvance(true);
      return;
    }
    const url = audioUrlsRef.current[sceneIdx] || await preloadAudio(sceneIdx);
    if (gen !== genRef.current) return;
    if (url) {
      const a = new Audio(url);
      a.playbackRate = 1.0;
      a.onended = () => {
        // End of narration → don't auto-advance. Prompt the coach to
        // press space (or click ▶) whenever they're ready to move on.
        if (gen === genRef.current && audioRef.current === a) {
          setAwaitingAdvance(true);
        }
      };
      audioRef.current = a;
      a.play().catch(() => { /* autoplay blocked — timer still runs */ });
      // Safety net if `ended` never fires: after ~30 s of no signal,
      // surface the prompt anyway so the coach isn't stuck.
      timerRef.current = setTimeout(() => {
        if (gen === genRef.current) setAwaitingAdvance(true);
      }, 30000);
      return;
    }
    // ── Browser-synth fallback (female English voice when available)
    if ("speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(s.caption);
        u.rate = 0.95; u.pitch = 1.05; // match the OpenAI cadence
        const voices = window.speechSynthesis.getVoices();
        const female = voices.find(v => /female|woman|samantha|karen|zira|susan|linda|nova/i.test(v.name)
                                        && v.lang?.startsWith("en"))
                    || voices.find(v => v.lang?.startsWith("en"));
        if (female) u.voice = female;
        u.onend = () => {
          if (gen === genRef.current) setAwaitingAdvance(true);
        };
        if (gen === genRef.current) window.speechSynthesis.speak(u);
      } catch {/* noop */}
    }
    // Prompt after a generous fallback window in case speechSynthesis
    // fails silently (some Linux headless browsers).
    timerRef.current = setTimeout(() => {
      if (gen === genRef.current) setAwaitingAdvance(true);
    }, 14000);
  }, [muted, preloadAudio, clearAll]);

  // React to scene changes. `playing` and `muted` are handled by
  // separate effects that mutate the live audio element in place, so
  // toggling them mid-scene doesn't restart the narration.
  useEffect(() => {
    if (!open) return;
    enterScene(scene, idx);
    return clearAll;
  }, [idx, open, scene, enterScene, clearAll]);

  // Pause / resume without restarting.
  useEffect(() => {
    const a = audioRef.current;
    if (a) {
      if (playing) a.play().catch(() => {/* autoplay blocked */});
      else         a.pause();
    }
    if ("speechSynthesis" in window) {
      try {
        if (playing) window.speechSynthesis.resume();
        else         window.speechSynthesis.pause();
      } catch {/* noop */}
    }
  }, [playing]);

  // Mute toggle applied to the live audio element in place.
  useEffect(() => {
    const a = audioRef.current;
    if (a) a.muted = muted;
  }, [muted]);

  // Auto-advance timer — once narration finishes and we're waiting for
  // the coach to press SPACE, give them 5 seconds to react. If nothing
  // happens, roll ahead on our own so the demo keeps flowing when it's
  // playing to an audience (e.g. mounted on a lobby screen). Pausing
  // the tour halts the countdown; pressing SPACE / arrow keys advances
  // immediately and clears the timer via the `awaitingAdvance` reset.
  useEffect(() => {
    if (!open || !awaitingAdvance || !playing) return;
    const t = setTimeout(() => advance(+1), 5000);
    return () => clearTimeout(t);
  }, [open, awaitingAdvance, playing, advance]);

  // Preload the first two scenes' audio the moment the modal opens so
  // Scene 1 starts talking with zero delay.
  useEffect(() => {
    if (!open) return;
    preloadAudio(0);
    preloadAudio(1);
  }, [open, preloadAudio]);

  // Clean up any lingering object URLs when the overlay closes.
  useEffect(() => {
    if (open) return;
    Object.values(audioUrlsRef.current).forEach((u) => {
      try { URL.revokeObjectURL(u); } catch {/* noop */}
    });
    audioUrlsRef.current = {};
    setIdx(0);
    setPlaying(true);
  }, [open]);

  // Keyboard shortcuts.
  //   Space  → advance if the narration finished (awaiting SPACE),
  //            else toggle play/pause.
  //   ← / →  → prev / next scene.
  //   Esc    → exit tour.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") advance(+1);
      else if (e.key === "ArrowLeft")  advance(-1);
      else if (e.key === " ") {
        e.preventDefault();
        if (awaitingAdvance) advance(+1);
        else                 setPlaying((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, advance, awaitingAdvance]);

  const progress = useMemo(() => Math.round(((idx + 1) / total) * 100), [idx, total]);

  if (!open) return null;

  return (
    <>
      {/* Watermark */}
      <div
        className="fixed top-3 right-3 z-[80] pointer-events-none"
        data-testid="demo-watermark"
      >
        <div className="inline-flex items-center gap-1.5 rounded-full bg-black/60 border border-violet-500/40 text-violet-200 px-3 py-1 backdrop-blur">
          <Sparkles size={12} className="text-violet-300" />
          <span className="text-[10.5px] uppercase tracking-widest">Demo in progress</span>
        </div>
      </div>

      {/* Floating caption card */}
      <div
        className="fixed bottom-4 right-4 z-[80] max-w-[440px] w-[92vw] sm:w-[440px]"
        data-testid="demo-overlay"
      >
        <div className="rounded-xl bg-[#0B1627]/95 border border-violet-500/40 shadow-2xl backdrop-blur p-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-violet-300 mb-0.5 flex items-center gap-1.5">
                Scene {idx + 1} / {total}
                {preloading && <Loader2 size={10} className="animate-spin opacity-70" />}
              </div>
              <div className="font-display text-sm text-slate-100 truncate">{scene.title}</div>
            </div>
            <button
              className="p-1 rounded hover:bg-white/10 text-slate-400"
              onClick={onClose}
              title="Exit demo (Esc)"
              data-testid="demo-exit"
            >
              <X size={14} />
            </button>
          </div>

          <div className="text-[12.5px] text-slate-200 leading-relaxed mb-3 min-h-[92px] max-h-[160px] overflow-y-auto">
            {scene.caption}
          </div>

          {/* When the narration for a scene finishes, we hold and wait
              for the coach to press SPACE (or the ▶ Next button) —
              lets them study the feature at their own pace instead of
              the tour racing ahead. Coach explicitly asked for this. */}
          {awaitingAdvance && (
            <div
              className="mb-3 rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-100 px-3 py-2 text-[11.5px] flex items-center gap-2"
              data-testid="demo-awaiting-advance"
            >
              <kbd className="px-1.5 py-0.5 rounded border border-cyan-400/60 bg-cyan-500/20 text-[10px] font-mono-data">SPACE</kbd>
              <span>or wait 5s — auto-advancing to the next feature…</span>
            </div>
          )}

          <div className="h-1 bg-slate-800 rounded-full overflow-hidden mb-3">
            <div
              className="h-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            <button className="p-1.5 rounded hover:bg-white/10 text-slate-300 disabled:opacity-40"
                    onClick={() => advance(-1)} disabled={idx === 0}
                    title="Previous (←)" data-testid="demo-prev"><SkipBack size={14} /></button>
            <button className="p-1.5 rounded bg-violet-500/20 hover:bg-violet-500/30 text-violet-200"
                    onClick={() => setPlaying(p => !p)}
                    title={playing ? "Pause (Space)" : "Play (Space)"} data-testid="demo-playpause">
              {playing ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <button className={`p-1.5 rounded ${awaitingAdvance ? "bg-cyan-500/25 hover:bg-cyan-500/40 text-cyan-100 ring-2 ring-cyan-400/50" : "hover:bg-white/10 text-slate-300"}`}
                    onClick={() => advance(+1)}
                    title="Next (→ or SPACE)" data-testid="demo-next"><SkipForward size={14} /></button>
            <div className="w-px h-4 bg-slate-700 mx-1" />
            <button className="p-1.5 rounded hover:bg-white/10 text-slate-300"
                    onClick={() => setMuted(m => !m)}
                    title={muted ? "Un-mute (nova voice)" : "Mute voice-over"} data-testid="demo-mute">
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <button className="p-1.5 rounded hover:bg-white/10 text-slate-300"
                    onClick={() => { setIdx(0); setPlaying(true); }}
                    title="Restart from scene 1" data-testid="demo-restart"><RefreshCw size={14} /></button>
            <span className="ml-auto text-[10px] text-slate-500 uppercase tracking-widest">
              Esc to exit
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
